import { useRef, useState } from "react";
import axios from "axios";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PhotoField({ label, value, onChange, hint, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image is too large — max 10 MB.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/api/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.path);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-xl border border-dashed border-[#3f3f46] p-5 bg-[#0c0c0c]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-display text-lg text-white">{label}</div>
          {hint && <div className="text-xs text-[#C4C4CC] mt-1">{hint}</div>}
        </div>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testid}-remove`}
            className="text-[#A1A1AA] hover:text-white"
            aria-label="Remove photo"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="mt-4 flex items-center gap-4">
        {value ? (
          <img
            src={`${API}/api/files/${value}`}
            alt="uploaded"
            className="w-24 h-24 rounded-lg object-cover border border-[#27272A]"
          />
        ) : (
          <div className="w-24 h-24 rounded-lg bg-[#111] border border-[#27272A] flex items-center justify-center text-[#8b8b92]">
            <Upload className="w-6 h-6" />
          </div>
        )}
        <div className="flex-1">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={testid}
            className="btn-ghost !py-2.5 !px-4 text-sm"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" /> {value ? "Replace" : "Upload"} photo
              </>
            )}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <div className="text-[10px] text-[#8b8b92] mt-2 tracking-widest uppercase">JPG · PNG · WebP · Max 10 MB</div>
        </div>
      </div>
    </div>
  );
}
